import { ReactElement, useContext, useEffect, useMemo } from 'react';

import { H3, TimeInput } from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Controller, FieldValues, Path, useWatch } from 'react-hook-form';

import { LocalizationContext } from 'localization';

import s from './ActionSchedule.module.scss';
import { IActionScheduleProps } from './interfaces';

export const ActionSchedule = <TFormValues extends FieldValues>({
  control,
  trigger,
  title = 'tariff.schedule',
  bordered,
}: IActionScheduleProps<TFormValues>): ReactElement => {
  const { t, lang } = useContext(LocalizationContext);

  const dayArr = useMemo(() => {
    const returnedDayName = [];

    for (let day = 1; day < 8; day++) {
      const weekday = dayjs().locale(lang).day(day).format('dd');

      returnedDayName.push({
        title: weekday.charAt(0).toUpperCase() + weekday.slice(1),
        value: day,
      });
    }

    return returnedDayName;
  }, [lang]);

  const start = useWatch({
    control,
    name: 'hours.start' as Path<TFormValues>,
  }).slice(0, 5);
  const end = useWatch({
    control,
    name: 'hours.end' as Path<TFormValues>,
  }).slice(0, 5);

  useEffect(() => {
    trigger(['hours.start', 'hours.end'] as Path<TFormValues>[]);
  }, [start, end, trigger]);

  return (
    <div className={s.container}>
      <H3>{t(title)}</H3>
      <Controller
        name={'days' as Path<TFormValues>}
        control={control}
        render={({ field }) => {
          const { value, onChange } = field;

          const daysArray: number[] = Array.isArray(value) ? value : [];

          const toggleDay = (day: number): void => {
            onChange(
              daysArray?.includes(day)
                ? daysArray.filter((d) => d !== day)
                : [...daysArray, day]
            );
          };

          return (
            <div className={s.day_block}>
              {dayArr.map((day) => {
                const active = value.includes(day.value);

                return (
                  <div
                    key={day.value}
                    className={cn(s.day, { [s.active]: active })}
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.title}
                  </div>
                );
              })}
            </div>
          );
        }}
      />
      <div className={s.time_block}>
        <Controller
          name={'hours.start' as Path<TFormValues>}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TimeInput
              {...field}
              error={error?.message}
              className={s.time}
              bordered={bordered}
            />
          )}
          rules={{
            validate: (value) => {
              if (end && !value) {
                return t('errors.required');
              }

              if (!value) {
                return undefined;
              }

              if (value.length < 5) {
                return t('errors.time.incorrect');
              }
            },
          }}
        />

        <div className={s.separator} />

        <Controller
          name={'hours.end' as Path<TFormValues>}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TimeInput
              {...field}
              error={error?.message}
              className={s.time}
              bordered={bordered}
            />
          )}
          rules={{
            validate: (value) => {
              if (start && !value) {
                return t('errors.required');
              }

              if (!value) {
                return undefined;
              }

              if (value.length < 5) {
                return t('errors.time.incorrect');
              }
            },
          }}
        />
      </div>
    </div>
  );
};
